import React, { useState, useEffect,useRef } from "react";
import './JDGuruPage.css';
import JdDetailsModal from './JDDetailsModal';
import JDEditableModal from './JDEditableModal';
import JDCreateModal from "./JDCreateModal";
import useAuthService from '../../services/AuthService';
import { useAuthContext } from "../Auth/AuthContext";
import config from "../../config.json";
import { Plus, ChevronDown } from "lucide-react";
import Notification from "./Notification";
import { useNavigate } from "react-router-dom";
import { Sparkles } from "lucide-react";



const JDGuruPage = () => {
    const authService = useAuthService();
    const websocketUrlForFileUpload = "wss://jvplqszjpi.execute-api.us-west-2.amazonaws.com/production/";
    const { userObjId, userAnalytics,setUserAnalytics, jdData, setJdData } = useAuthContext();
    const [showNotification, setShowNotification] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState('');
    const navigate = useNavigate();
    // Generate random number function
    const generateRandom5DigitNumber = () => {
        return Math.floor(10000 + Math.random() * 90000);
    };

    // States
    const [formData, setFormData] = useState({
        "job_role": "",
        company: "",
        job_type: "",
        job_description: {
            about_us: "",
            job_overview: "",
            key_responsibilities: "",
            required_qualification_experience: "",
            preferred_skills: ""
        },
        experience: "",
        salary: "",
        location: "",
        date: new Date().toISOString().split("T")[0],
        time: new Date().toTimeString().split(" ")[0],
        jd_id: generateRandom5DigitNumber()
    });
    const [isModalOpen, setModalOpen] = useState(false);
    const [isEditModalOpen, setEditModalOpen] = useState(false);
    const [selectedJdId, setSelectedJdId] = useState(null);
    const [isCreateModalOpen, setCreateModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);


    const [showDropDown, setShowDropDown]= useState(false);
    const dropdownRef=useRef(null);
    const buttonRef= useRef(null);
    useEffect(() => {
        const handleOutsideClick = (event) => {
          // If click is outside the button and dropdown, close the dropdown.
          if (
            dropdownRef.current && !dropdownRef.current.contains(event.target) &&
            buttonRef.current && !buttonRef.current.contains(event.target)
          ) {
            setShowDropDown(false);
          }
        };
    
        if (showDropDown) {
          document.addEventListener('mousedown', handleOutsideClick);
        } 
        return () => {
          document.removeEventListener('mousedown', handleOutsideClick);
        };
      }, [showDropDown]);

      const fetchAnalyticsDetails = async () => {
        try {
            setUserAnalytics({})
            const response = await fetch(`${config.restAPI}/sessions/getuserAnalytics/`, {
                method: "POST",
                credentials:"include",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({ user_obj_id:userObjId }),
            });

            if (!response.ok) {
                throw new Error("Failed to fetch profile details");
            }
            const data = await response.json();
            setUserAnalytics(data);
        } catch (error) {
            console.error("Error fetching profile details:", error);
        }
    };

    const fetchJobRoles = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${config.restAPI}/jd/getJobs/`, {
                method: "POST",
                credentials:"include",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ user_obj_id:userObjId }),
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch job roles. Status: ${response.status}`);
            }

            const data = await response.json();
            const sortedData = data.job_descriptions.sort((a, b) => {
                const aDate = new Date(a.created_at);
                const bDate = new Date(b.created_at);
                return bDate - aDate; // Latest first
            });
    
            setJdData(sortedData || []);
        } catch (error) {
            console.error("Error fetching job roles:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchJobRoles();
    }, []);


    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (!file) {
            console.error("No file selected.");
            return;
        }
        handleFileUpload(file);
        event.target.value = null;
    };

    const handleFileUpload = (file) => {

        setIsUploading(true); // Set only when uploading a file
        setCreateModalOpen(true);
        setShowDropDown(false);
        const websocketInstance = new WebSocket(websocketUrlForFileUpload);

        websocketInstance.onopen = () => {
            const message = { action: "botguru_sendmessage", message: "get data" };
            sendWebSocketMessageWithRetry(websocketInstance, message);
        };

        websocketInstance.onmessage = (event) => handleWebSocketMessageForFileUpload(event, file);

        websocketInstance.onerror = (error) => {
            console.error("WebSocket error for File Upload:", error);
        };

        websocketInstance.onclose = () => {
            // console.log("WebSocket connection closed for File Upload.");
        };
    };

    const sendWebSocketMessageWithRetry = (websocketInstance, message, retries = 3, interval = 1000) => {
        let attempts = 0;

        const sendMessage = () => {
            if (websocketInstance.readyState === WebSocket.OPEN) {
                websocketInstance.send(JSON.stringify(message));
            } else if (attempts < retries) {
                attempts++;
                setTimeout(sendMessage, interval);
            } else {
                console.error("Failed to send WebSocket message after multiple attempts.");
            }
        };

        sendMessage();
    };

    const handleWebSocketMessageForFileUpload = async (event, file) => {
        try {
            const data = JSON.parse(event.data);

            if (data.connectionid) {
                try {
                    await authService.jobDescriptionExtractor({
                        file,
                        connectionId: data.connectionid,
                        user_obj_id:userObjId,
                    });
                } catch (error) {
                    console.error("Error uploading file:", error);
                    setNotificationMessage(error?.data.error);
                    setShowNotification(true);
                    setLoading(false);
                    setCreateModalOpen(false);
                }
            }

            if (data.message) {
                try {
                    const parsedMessage = JSON.parse(data.message);
                    // Update form data
                    setFormData(prev => ({
                        ...prev,
                        "job_role": parsedMessage.job_role || prev["job_role"],
                        company: parsedMessage.company || prev.company,
                        job_type: parsedMessage.job_type || prev.job_type,
                        experience: parsedMessage.job_description.experience || prev.experience,
                        salary: parsedMessage.job_description.salary || prev.salary,
                        location: parsedMessage.job_description.location || prev.location,  
                        job_description: {
                            ...prev.job_description,
                            about_us: parsedMessage.job_description.about_us || prev.job_description.about_us,
                            job_overview: parsedMessage.job_description.job_overview || prev.job_description.job_overview,
                            key_responsibilities: parsedMessage.job_description.key_responsibilities || prev.job_description.key_responsibilities,
                            required_qualification_experience: parsedMessage.job_description.required_qualification_experience || prev.job_description.required_qualification_experience,
                            preferred_skills: parsedMessage.job_description.preferred_skills || prev.job_description.preferred_skills
                        },
                        jd_id: generateRandom5DigitNumber()
                    }));
                    setIsUploading(false); // Ensure reset when closed
                } catch (parseError) {
                    console.error("Error parsing message:", parseError);
                }
            }
        } catch (error) {
            console.error("Error handling WebSocket message:", error);
        }
    };



    const openModal = (_id) => {
        setSelectedJdId(_id);
        setModalOpen(true);
    };

    const openEditModal = (_id) => {
        setSelectedJdId(_id);
        setEditModalOpen(true);
    };

    const fileInputRef = useRef(null);

    
    const handleUploadJDClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        } else {
            console.error("File input field not found.");
        }
    };
    

    const handleCreateJDClick = () => {
            setCreateModalOpen(true);
    };

    const renderCard = (data) => {
        return (
            <div className="jd-guru-card" key={data._id} >
                <div className="jd-card-details">
                    <div className="jd-heading">
                        <div className="jd-title">
                            <h3>{data.job_role}</h3>   
                            <span className="jd-id-badge">
                            <span className="jd-id-number">#{data.jd_id}</span>
                            </span>    
                        </div>
                    
                        <div className="jd-info-row">
                            <div className="jd-info-item">
                                <div className="jd-info-value">
                                    <i className="far fa-clock"></i>
                                    {new Date(data.created_at).toLocaleDateString('en-US', { day:'numeric',year: 'numeric', month: 'short' })}
                                </div>
                            </div>
                            <div className="jd-info-item">
                                <div className="jd-info-value">
                                    <i className="fas fa-briefcase"></i>
                                    {data.company||'Not Provided'}
                                </div>
                            </div>
                            <div className="jd-info-item">
                                <div className="jd-info-value">
                                    <i className="fas fa-briefcase"></i>
                                    {data.experience||'Not Provided'}
                                </div>
                            </div>

                        </div>
                    </div>
                        <div className="jd-action">
                            <span className="jd-file-type" onClick={(e) => {
                                e.stopPropagation();
                                openEditModal(data._id)}}>
                                <i className="fas fa-pen  view-icon"></i>
                            </span>
                            <span className="jd-file-type"  onClick={(e) => {
                                e.stopPropagation();
                                openModal(data._id);
                                }} >
                                    <i className="fas fa-chevron-right view-icon"  ></i>
                            </span>
                        </div>
                </div>
            </div>
        );
    }; 


    // Calculate the total sessions available by summing prepbot and mock_bot totals
    const totalSessions = (userAnalytics?.total_jd_extractor || 0) 
    const usedSessions = (userAnalytics?.used_jd_extractor || 0)

    // Determine if the user has available sessions
    const hasSessions = totalSessions > usedSessions;

    return (
        <div className="jd-guru-page" style={{position:"relative"}}>
            <div className="hero-section">
                <div className="hero-content">
                    <div className="hero-left">
                        <h1 className="hero-title">
                            Your Personalized JD Bot
                        </h1>
                        <div className="hero-description">
                            <span>Create clear, engaging job descriptions in minutes! Use our AI-powered platform to customize templates and highlight essential skills. 
                            Generate professional JDs with perfect formatting and ensure consistency using JDExpert!</span>
                        </div>
                    </div>
                </div>
            </div>
                  
            <input
                ref={fileInputRef}  // Attach useRef
                type="file"
                accept=".docx,.pdf"
                style={{ display: 'none' }}
                onChange={handleFileChange}
            />
            <div className="job-upload-create-btn-container"
            >
                <button className="job-upload-create-btn" ref={buttonRef} 
                     onClick={()=>setShowDropDown(true)} 
                     onMouseEnter={() => setShowDropDown(true)}
                     onMouseLeave={() => {
                        setTimeout(() => {
                            if (!dropdownRef.current?.matches(':hover')) {
                                setShowDropDown(false);
                            }
                        }, 100);
                    }}
                >

                <Plus className="btn-icon" />
                <span>Add New Job</span>
                <ChevronDown className={`drop-down-icon ${showDropDown ? "active" : ""}`} />
                    
                </button>

                {showDropDown && (
                    <div className="job-drop-down-container" ref={dropdownRef}  onMouseEnter={() => setShowDropDown(true) }
                    onMouseLeave={() => setShowDropDown(false)}  onClick={()=>setShowDropDown(false)}>
                        <div className="job-upload-create-cards">

                            <div className="drop-down-option" 
                                onClick={() => {
                                    if (hasSessions) {
                                        handleUploadJDClick()
                                    } else {
                                        navigate("/pricing", { state: { activeTabName: 'AI JD Generator' } });
                                    }
                                }}
                            >
                                {hasSessions?(
                                    <>
                                        <i className="fas fa-cloud-upload-alt"></i> Upload JD
                                    </>
                                    ) : (
                                        <>
                                        <div className="upload-jd-access">
                                            <div className="upload-jd-option">
                                            <i className="fas fa-cloud-upload-alt"></i>
                                            Upload JD
                                            </div>
                                            <i className="fas fa-lock"></i>
                                         </div>
                                        </>
                                )}
                                
                            </div>
                            <div className="drop-down-option" onClick={handleCreateJDClick}>
                                <i className="fas fa-file-alt"></i> Create JD
                            </div>
                        </div>
                    </div>
                )}
            </div> 
            <div className="jd-guru-container">
            {loading ? null : (jdData.length === 0 ? (
                <div className="no-session-box">
                <div className="no-session-content">
                    <Sparkles color="#BE05FA" />
                    <h3>No Job Descriptions</h3>
                    <p>Start creating a new job description.</p>
                </div>
                </div>
            ) : (   
                jdData.map((jd) => renderCard(jd))
            )
            )}
            </div>

            <JdDetailsModal 
                isOpen={isModalOpen} 
                closeModal={() => setModalOpen(false)} 
                jdId={selectedJdId} 
                refreshJDList={fetchJobRoles}
                jdData={jdData} 
            />
            <JDEditableModal 
                isOpen={isEditModalOpen} 
                closeModal={() => setEditModalOpen(false)} 
                jdId={selectedJdId} 
                refreshJDList={()=>{
                    fetchJobRoles()
                    fetchAnalyticsDetails()
                }}
                jdData={jdData}
            />
            <JDCreateModal
                isOpen={isCreateModalOpen}
                closeModal={() => setCreateModalOpen(false)}
                refreshJDList={()=>{
                    fetchJobRoles()
                    fetchAnalyticsDetails()
                }}
                initialFormData={formData} 
                isLoading={isUploading}
            />
            {showNotification && (
                <Notification 
                    message={notificationMessage}
                    onClose={() => setShowNotification(false)}
                />
            )}
        </div>
    );
};

export default JDGuruPage;